import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { subscribe, publish, EVENTS } from '@sendible/event-manager';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide, SwiperRef, SwiperProps } from 'swiper/react';
import 'swiper/css';
import { Keyboard, Navigation } from 'swiper/modules';
import { Icon, Input, Spinner, useKeyDown } from '@sendible/design-system';
import { composeTabs, MediaIdType, MediaPreviewProps } from '@sendible/shared-state-bridge';
import { ActionsBar, LightboxStyled, SwiperContainerBox, InputContainer } from './index.styles';
import { ActionButton } from './components';
import { PdfThumbnail } from '../MediaSystem/components/MediaBelt/PdfThumbnail';
import { useMediaControls } from '../../hooks/useMediaControls';
import { getYouTubeEmbedUrl, isYouTubeVideo } from '../../utils';
import { composeMediaInteractions } from '../MediaSystem/composeMediaInteractions';

interface Props extends MediaPreviewProps {
  autoFocusTitleInput?: boolean;
  onActionClick?: (action: string, mediaId: MediaIdType) => void | Promise<void>;
  onClose: () => void;
  shouldShowActions?: boolean;
  supportedActions?: string[];
  tabId?: composeTabs;
}

export const PREVIEW_LIGHTBOX_TEST_ID = 'previewmedias_test';

export const MediaPreview = ({
  autoFocusTitleInput,
  initialMediaIndex,
  mediaList,
  onActionClick,
  onClose,
  shouldShowActions,
  supportedActions,
  tabId,
}: Props) => {
  const [currentSlide, setCurrentSlide] = useState(initialMediaIndex);
  const [beforeEditLength, setBeforeEditLength] = useState(mediaList.length);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const swiperRef = useRef(null) as unknown as React.RefObject<SwiperRef>;
  const { dropdownActions, canEditTitle } = useMediaControls(tabId);
  const { t } = useTranslation('media_library');

  const currentMedia = mediaList[currentSlide];
  const currentMediaType = currentMedia?.type === 'pdf' ? 'document' : currentMedia?.type;

  // NOTE: resets the title input whenever the visible media changes (e.g. swiping between
  // PDFs) by comparing against the previous render's key during render, rather than in a
  // useEffect, so the reset lands in the same commit as the media change instead of a
  // follow-up render. See https://react.dev/learn/you-might-not-need-an-effect
  const mediaResyncKey = `${currentMedia?.id ?? ''}|${currentMedia?.fileDisplayName ?? ''}|${currentMedia?.name ?? ''}`;
  const [titleInputValue, setTitleInputValue] = useState(currentMedia?.fileDisplayName || currentMedia?.name || '');
  const [lastMediaResyncKey, setLastMediaResyncKey] = useState(mediaResyncKey);

  if (mediaResyncKey !== lastMediaResyncKey) {
    setLastMediaResyncKey(mediaResyncKey);
    setTitleInputValue(currentMedia?.fileDisplayName || currentMedia?.name || '');
  }

  const showTitleInput = !!(shouldShowActions && tabId && currentMedia?.type === 'pdf' && canEditTitle);

  // We only show the "Title" option in the drop down; we exclude it from the Action Bar
  const controls = (dropdownActions[currentMedia?.isGif ? 'gif' : currentMediaType] || []).filter((control) => control.action !== 'title');
  const filteredControls =
    shouldShowActions && supportedActions?.length ? controls.filter((control) => supportedActions.includes(control.action)) : controls;

  const isEditing = mediaList.length < beforeEditLength;
  const TAG = 'tag';
  const hasTag = !!controls.find((item) => item.action === TAG) && !!currentMedia.igUserTags?.length;

  if (hasTag) {
    const tagOption = controls.find((option) => option.action === TAG);

    if (tagOption) {
      tagOption.altLabel = currentMedia.igUserTags?.length.toString() || '0';
    }
  }

  const onSlideChange = () => {
    const videos = document.querySelectorAll('video');
    const iframes = document.querySelectorAll('iframe');

    [...videos].forEach((video) => video.pause());

    // NOTE: send pause message to youtube videos on slide
    [...iframes].forEach((iframe) => {
      if (iframe.src.includes('youtube.com/embed/')) {
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', 'https://www.youtube.com');
        }
      }
    });
  };

  const config: SwiperProps = {
    className: 'lightbox__swiper',
    grabCursor: !!mediaList.length,
    initialSlide: initialMediaIndex || 0,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    modules: [Keyboard, Navigation],
    navigation: {
      prevEl: '.swiper-button-prev',
      nextEl: '.swiper-button-next',
    },
    onSlideChange,
  };

  const commitTitleInputValue = (updatedFileDisplayName: string) => {
    if (!tabId || currentMedia?.id === undefined || currentMedia?.id === null) return;

    const trimmedValue = updatedFileDisplayName.trim();
    const currentValue = currentMedia.fileDisplayName || currentMedia.name;
    const newValue = trimmedValue || currentMedia.name;

    setTitleInputValue(newValue);

    if (newValue === currentValue) return;

    composeMediaInteractions.updateFileDisplayName(currentMedia.id as MediaIdType, tabId, newValue);
    window.Backbone.trigger('customised-tab');
  };

  const requestClose = () => {
    const { documentElement } = document;

    // NOTE: clicking the clear icon only blanks the input locally - it doesn't
    // blur the native input (focus moves to the clear button instead), so
    // closing the lightbox must commit any pending title edit itself rather
    // than relying on a blur event that may never fire.
    if (showTitleInput) {
      commitTitleInputValue(titleInputValue);
    }

    documentElement.style.overflow = 'auto';

    onClose();
  };

  const openPdfInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useKeyDown(() => {
    requestClose();
  }, 'Escape');

  useEffect(() => {
    const { documentElement } = document;

    documentElement.style.overflow = 'hidden';

    publish(EVENTS.MEDIA_PREVIEW_TOGGLE, { isOpen: true });

    return () => {
      documentElement.style.overflow = 'auto';
      publish(EVENTS.MEDIA_PREVIEW_TOGGLE, { isOpen: false });
    };
  }, []);

  useEffect(() => {
    const unsubscribeEditor = subscribe(EVENTS.OPEN_PHOTO_EDITOR, () => {
      setBeforeEditLength(mediaList.length);
    });

    return () => {
      unsubscribeEditor();
    };
  }, [mediaList]);

  return createPortal(
    <LightboxStyled
      darkmode
      close={requestClose}
      testId={PREVIEW_LIGHTBOX_TEST_ID}
      ariaCloseLabel={t('aria_close_lightbox')}
    >
      <SwiperContainerBox orientation="row">
        <Swiper
          {...config}
          ref={swiperRef}
          onActiveIndexChange={(swiper: { realIndex: number }) => setCurrentSlide(swiper.realIndex)}
        >
          {mediaList?.map((media) => {
            if (isEditing)
              return (
                <div
                  key={media.url}
                  className="swiper-lazy-preloader"
                />
              );

            return (
              <SwiperSlide key={media.url}>
                {media.type === 'video' && isYouTubeVideo(media.url) && (
                  <iframe
                    className="swiper-lazy media-preview-iframe"
                    src={getYouTubeEmbedUrl(media.url)}
                    data-src={getYouTubeEmbedUrl(media.url)}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="YouTube video player"
                    style={{
                      border: 'none',
                      width: '100%',
                      maxWidth: '800px',
                      height: 'auto',
                      aspectRatio: '16/9',
                      display: 'block',
                    }}
                  />
                )}
                {media.type === 'video' && !isYouTubeVideo(media.url) && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video
                    className="swiper-lazy"
                    controls
                    tabIndex={-1}
                  >
                    <source
                      src={media.url}
                      data-testid={media.name}
                      data-src={media.url}
                    />
                  </video>
                )}
                {media.type === 'image' && (
                  <img
                    className="swiper-lazy"
                    src={media.url}
                    data-testid={media.name}
                    data-src={media.url}
                    alt=""
                  />
                )}
                {media.type === 'pdf' && (
                  <div
                    className="media-preview-pdf"
                    data-testid={media.name}
                  >
                    <PdfThumbnail
                      url={media.url}
                      altText={media.name}
                      fillParent={false}
                      onClick={() => openPdfInNewTab(media.url)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openPdfInNewTab(media.url);
                        }
                      }}
                      fit="contain"
                      role="button"
                      scale={1}
                      tabIndex={0}
                      testId={`${media.name}-canvas`}
                    />
                  </div>
                )}

                <div className="swiper-lazy-preloader" />
              </SwiperSlide>
            );
          })}
          <button
            type="button"
            className="swiper-button-prev"
            data-testid="swiper-button-prev"
            disabled={isProcessingAction}
            tabIndex={0}
            aria-label={t('aria_move_slider_left') as string}
          >
            <Icon
              color="brandLight"
              name="arrow_left"
            />
          </button>
          <button
            type="button"
            className="swiper-button-next"
            data-testid="swiper-button-next"
            disabled={isProcessingAction}
            tabIndex={0}
            aria-label={t('aria_move_slider_right') as string}
          >
            <Icon
              color="brandLight"
              name="arrow_right"
            />
          </button>
        </Swiper>
        {showTitleInput && (
          <InputContainer>
            <Input
              adaptWidthToText={true}
              ariaLabel={t('media_preview_pdf_title_input_label')}
              autoComplete={'off'}
              autoFocus={autoFocusTitleInput}
              blur={commitTitleInputValue}
              change={(value) => setTitleInputValue(value)}
              darkmode
              id={'pdf-title-input'}
              initialValue={titleInputValue}
              label={t('media_preview_pdf_title_input_label')}
              maxLength={500}
              minWidth="5rem"
              maxWidth="26rem"
              size="sm"
              testId="media-preview-title-input"
              type="text"
            />
          </InputContainer>
        )}
        {shouldShowActions && (
          <ActionsBar data-testid="action-bar">
            {currentMedia?.id ? (
              filteredControls.map(({ action, icon, label, altLabel }) => (
                <ActionButton
                  icon={icon}
                  label={label}
                  altLabel={altLabel}
                  isDisabled={isEditing || isProcessingAction}
                  isLoading={isProcessingAction}
                  onClick={async () => {
                    if (onActionClick && currentMedia.id !== undefined && currentMedia.id !== null) {
                      const mediaId = typeof currentMedia.id === 'string' ? currentMedia.id : currentMedia.id;

                      if (action === 'tag') {
                        requestClose();
                        setTimeout(() => {
                          onActionClick(action, mediaId as MediaIdType);
                        }, 0);

                        return;
                      }

                      setIsProcessingAction(true);

                      try {
                        await onActionClick(action, mediaId as MediaIdType);
                      } finally {
                        setIsProcessingAction(false);
                      }

                      if (action === 'remove') {
                        if (mediaList.length === 1) {
                          onClose();
                        }
                        setBeforeEditLength(mediaList.length - 1);
                      }
                    }
                  }}
                  key={action}
                />
              ))
            ) : (
              <Spinner size="md" />
            )}
          </ActionsBar>
        )}
      </SwiperContainerBox>
    </LightboxStyled>,
    document.getElementById('lightbox-root') as HTMLElement
  );
};
